import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { strings } from '/imports/strings';
import { css } from '/imports/ui/css';
import { Button } from '/imports/ui/Button';
import { Select } from '/imports/ui/Select';
import { clientDownloadUrl } from '/imports/utils';
import { toastOnError } from '/imports/ui/utils';
import { ProjectManagementPipelineRow, projectManagementMoney as money, projectManagementFinancesExport } from '/imports/api/projectManagement';
import { ProjectManagementLifecycle } from '/imports/api/projectManagementLifecycles';

const FinancesStyled = styled.div`
  height: 100%;
  overflow: auto;
  /* name · bars · Actual · Estimated. The header row shares the grid so its labels sit over the figure columns. */
  /* Right padding matches the legend row's, so the figure columns align with the Export button above them. */
  .proj { display: grid; grid-template-columns: 14rem 1fr 6.5rem 8rem; gap: 1rem; align-items: center; padding: 0.6rem 0.75rem 0.6rem 0; border-top: 1px solid ${css.grey7}; }
  .proj.hdr { border-top: none; padding: 0 0.75rem 0.25rem 0; font-size: 0.75rem; color: ${css.grey4}; text-transform: uppercase; letter-spacing: 0.04em; }
  .proj .name { font-weight: 600; color: ${css.primaryColor}; cursor: pointer; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  /* The project's Luminia id under its name — the column is 14rem, so it rides beneath rather than beside. */
  .proj .luminia-id { display: block; font-weight: normal; font-size: 0.72rem; color: ${css.grey4}; }
  .bars, .figures { display: flex; flex-direction: column; gap: 0.35rem; }
  /* A fixed row height, shared by bar rows and figure rows, keeps each amount level with its bar. */
  .barrow, .figures div { height: 1.1rem; display: flex; align-items: center; font-size: 0.8rem; }
  .barrow { gap: 0.6rem; }
  .figures { text-align: right; color: ${css.grey2}; }
  .figures div { justify-content: flex-end; }
  .proj.hdr .figures { text-align: right; }
  .proj.hdr .figures div { white-space: nowrap; }
  .barrow .lbl { width: 2.5rem; color: ${css.grey3}; }
  .bartrack { position: relative; flex: 1; height: 0.85rem; background: ${css.grey8}; border-radius: 3px; }
  .bar { position: absolute; top: 0; bottom: 0; border-radius: 3px; }
  .bar.estimated { border: 1px solid; background: transparent; opacity: 0.55; }
  /* Square the edges either side of the contract/change-order join so the two segments read as one bar. */
  .bar.split { border-top-right-radius: 0; border-bottom-right-radius: 0; }
  /* A change order is small next to a build: a $12k CO against a portfolio scaled to $10.8m is 0.1% of
     the track, i.e. one pixel. The floor keeps a real change order visible; it can push the segment a
     hair past the true total, which is the accepted trade for not rendering money as nothing. */
  .bar.co { border-top-left-radius: 0; border-bottom-left-radius: 0; min-width: 0.35rem; }
  /* Painted SOLID rather than left as an outline, and at full opacity. A change order carries a single
     value — the change-order modal has no estimated/actual split — so it lands on the estimate track and
     usually nowhere else. An outline at 55% opacity, a few pixels wide, is invisible; this is the only
     place most change orders can ever show. */
  .bar.estimated.co { background: ${css.red2}; opacity: 1; }
  /* Legend text on the left; Sort by + Export on the right, sharing one line. */
  .legend { display: flex; justify-content: space-between; align-items: center; font-size: 0.75rem; color: ${css.grey4}; margin: 0.75rem 0 1rem; padding-right: 0.75rem; }
  .legend .co-key { color: ${css.red2}; }
  .legend .tools { display: flex; align-items: center; gap: 0.75rem; }
  .legend .tools label { display: flex; align-items: center; gap: 0.4rem; white-space: nowrap; }
`;

// Sort orders for the list: 'default' is the original net-actual ranking; each money figure sorts both ways.
type SortKey = 'default' | 'name'
  | 'sourcesActualDesc' | 'sourcesActualAsc' | 'sourcesEstimatedDesc' | 'sourcesEstimatedAsc'
  | 'usesActualDesc' | 'usesActualAsc' | 'usesEstimatedDesc' | 'usesEstimatedAsc';
type CashField = 'inActual' | 'inEstimated' | 'outActual' | 'outEstimated';
const byCash = (field: CashField, direction: 'asc' | 'desc') => (a: ProjectManagementPipelineRow, b: ProjectManagementPipelineRow) =>
  (direction === 'asc' ? 1 : -1) * (a.cash[field] - b.cash[field]);
const comparators: { [k in SortKey]: (a: ProjectManagementPipelineRow, b: ProjectManagementPipelineRow) => number } = {
  default: (a, b) => b.cash.netActual - a.cash.netActual,
  name: (a, b) => (a.project.name || '').localeCompare(b.project.name || ''),
  sourcesActualDesc: byCash('inActual', 'desc'),
  sourcesActualAsc: byCash('inActual', 'asc'),
  sourcesEstimatedDesc: byCash('inEstimated', 'desc'),
  sourcesEstimatedAsc: byCash('inEstimated', 'asc'),
  usesActualDesc: byCash('outActual', 'desc'),
  usesActualAsc: byCash('outActual', 'asc'),
  usesEstimatedDesc: byCash('outEstimated', 'desc'),
  usesEstimatedAsc: byCash('outEstimated', 'asc'),
};

interface Props {
  rows: ProjectManagementPipelineRow[];
  lifecycle: ProjectManagementLifecycle;
}

/** Cross-project finances view: per-project money-in/out bars (estimated vs actual) with an xlsx export. */
export const ProjectManagementFinancesView = ({ rows }: Props) => {
  const navigate = useNavigate();
  const [sortKey, setSortKey] = useState<SortKey>('default');
  const [exporting, setExporting] = useState(false);
  // "Sources - invoiced (desc.)" etc., assembled from the shared words so the eight labels stay consistent
  // and match the column headers: Invoiced (booked) and Project Payment (estimate), for Sources and Uses alike.
  const cashLabel = (side: string, figure: string, direction: string) => `${side} - ${figure.toLowerCase()} (${direction})`;
  const sortOptions: { value: SortKey, label: string }[] = [
    { value: 'default', label: strings.Default() },
    { value: 'sourcesActualDesc', label: cashLabel(strings.CashSources(), strings.Invoiced(), strings.Desc()) },
    { value: 'sourcesActualAsc', label: cashLabel(strings.CashSources(), strings.Invoiced(), strings.Asc()) },
    { value: 'sourcesEstimatedDesc', label: cashLabel(strings.CashSources(), strings.ProjectPayment(), strings.Desc()) },
    { value: 'sourcesEstimatedAsc', label: cashLabel(strings.CashSources(), strings.ProjectPayment(), strings.Asc()) },
    { value: 'usesActualDesc', label: cashLabel(strings.CashUses(), strings.Invoiced(), strings.Desc()) },
    { value: 'usesActualAsc', label: cashLabel(strings.CashUses(), strings.Invoiced(), strings.Asc()) },
    { value: 'usesEstimatedDesc', label: cashLabel(strings.CashUses(), strings.ProjectPayment(), strings.Desc()) },
    { value: 'usesEstimatedAsc', label: cashLabel(strings.CashUses(), strings.ProjectPayment(), strings.Asc()) },
    { value: 'name', label: strings.Name() },
  ];

  const scaleMax = Math.max(1, ...rows.flatMap((r) => [r.cash.inEstimated, r.cash.inActual, r.cash.outEstimated, r.cash.outActual]));
  const sorted = [...rows].sort(comparators[sortKey]);

  /** Downloads the list as a formatted workbook — same rows and order as rendered, every status included.
   *  The server re-sums the money and writes the file; the figures land as real numbers under a currency
   *  format, so they stay summable in the spreadsheet rather than arriving as pre-formatted text. */
  const exportXlsx = toastOnError(async () => {
    setExporting(true);
    try {
      // The url already carries the name the file saves under — the server names it, because the /nfs
      // Content-Disposition is what decides it and an anchor's download attribute cannot override that.
      clientDownloadUrl(await projectManagementFinancesExport.call({ projectIds: sorted.map((r) => r.project._id) }));
    }
    finally {
      setExporting(false);
    }
  });

  /**
   * One estimated/actual bar. `changeOrder` splits the SAME totals into contract + change orders rather
   * than extending the bar: an accepted change order is already a money-out event inside `estimated`
   * and `actual`, so the red segment is carved off the end and the full length still reads as the total.
   */
  const bar = (label: string, estimated: number, actual: number, color: string, changeOrder?: { estimated: number, actual: number }) => {
    const pct = (v: number) => `${(v / scaleMax) * 100}%`;
    const co = changeOrder && (changeOrder.estimated > 0 || changeOrder.actual > 0) ? changeOrder : undefined;
    // Clamped: a change-order line edited to exceed its own outflow total would otherwise give the
    // contract segment a negative width, which CSS drops — taking the whole bar with it.
    const base = {
      estimated: Math.max(0, estimated - (co?.estimated || 0)),
      actual: Math.max(0, actual - (co?.actual || 0)),
    };
    return (
      <div className="barrow">
        <span className="lbl">{label}</span>
        <span className="bartrack">
          {/* A zero-width segment is skipped, not drawn: its 1px border would still paint a sliver of the
              wrong colour at the left edge — visible when a project's whole spend is change orders. */}
          {base.estimated > 0 && <span className={`bar estimated${co ? ' split' : ''}`} style={{ width: pct(base.estimated), borderColor: color }} />}
          {!!co?.estimated && <span className="bar estimated co" style={{ left: pct(base.estimated), width: pct(co.estimated), borderColor: css.red2 }} />}
          {base.actual > 0 && <span className={`bar${co ? ' split' : ''}`} style={{ width: pct(base.actual), background: color }} />}
          {!!co?.actual && <span className="bar co" style={{ left: pct(base.actual), width: pct(co.actual), background: css.red2 }} />}
        </span>
      </div>
    );
  };

  return (
    <FinancesStyled>
      <div className="legend">
        <span>◻ {strings.ProjectPayment()} · ▮ {strings.Invoiced()} · <span className="co-key">▮</span> {strings.ChangeOrders()}</span>
        <div className="tools">
          <label>{strings.SortBy()}<Select value={sortKey} options={sortOptions} onChange={(v) => setSortKey(v as SortKey)} /></label>
          <Button className="primary" onClick={() => exportXlsx()} disabled={!sorted.length || exporting}>{exporting ? strings.Exporting() : strings.Export()}</Button>
        </div>
      </div>
      {/* Column labels for the figures — the partner's terms for both rows: Invoiced (what has been booked) and
          Project Payment (the estimate). */}
      <div className="proj hdr">
        <span /><span />
        <span className="figures">{strings.Invoiced()}</span>
        <span className="figures">{strings.ProjectPayment()}</span>
      </div>
      {
        sorted.map((r) => (
          <div className="proj" key={r.project._id}>
            <span className="name" onClick={() => navigate(`/project-management/projects/${r.project._id}`)}>
              {r.project.name}
              {r.project.luminiaId && <span className="luminia-id">{r.project.luminiaId}</span>}
            </span>
            <span className="bars">
              {bar(strings.CashSource(), r.cash.inEstimated, r.cash.inActual, css.green)}
              {bar(strings.CashUse(), r.cash.outEstimated, r.cash.outActual, css.orange, {
                estimated: r.cash.outChangeOrderEstimated, actual: r.cash.outChangeOrderActual,
              })}
            </span>
            <span className="figures">
              <div>{money(r.cash.inActual)}</div>
              <div>{money(r.cash.outActual)}</div>
            </span>
            <span className="figures">
              <div>{money(r.cash.inEstimated)}</div>
              <div>{money(r.cash.outEstimated)}</div>
            </span>
          </div>
        ))
      }
    </FinancesStyled>
  );
};
